import { AJUDA_PRODUCAO } from "@/lib/ajuda/dialogos/producao";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { STAGE_COLORS } from "./production-stage-colors";
import { SeletorDeCorDoEstagio } from "./SeletorDeCorDoEstagio";

type Props = {
  aberto: boolean;
  /** Cria na trilha do MODELO em vez da do serviço — mesma tela, mutation diferente. */
  modelo?: boolean;
  onFechar: () => void;
  /** Recebe o estágio recém-criado — quem abriu decide o que fazer (a planilha já aplica no
   *  serviço que estava sendo editado). */
  onCriado: (estagio: { id: string; label: string }) => void;
};

/**
 * Criar um estágio de produção sem sair de onde se está — o atalho do dropdown da planilha.
 * A gestão completa (renomear, reordenar, excluir, comportamento ao concluir) continua no
 * quadro de Estágios; aqui só o essencial pra destravar quem está no meio de uma edição.
 */
export function NovoEstagioDialog({ aberto, modelo = false, onFechar, onCriado }: Props) {
  const utils = trpc.useUtils();
  const [label, setLabel] = useState("");
  const [cor, setCor] = useState(STAGE_COLORS[0]);

  useEffect(() => {
    if (aberto) {
      setLabel("");
      setCor(STAGE_COLORS[0]);
    }
  }, [aberto]);

  const aoCriar = (estagio: any) => {
    utils.prosthesis.stages.list.invalidate();
    utils.prosthesis.modelStages.list.invalidate();
    toast.success(`${modelo ? "Etapa" : "Estágio"} "${estagio.label}" criado.`);
    onCriado({ id: estagio.id, label: estagio.label });
    onFechar();
  };
  const aoFalhar = (e: { message?: string }) => toast.error(e.message || "Erro ao criar.");
  const criarServico = trpc.prosthesis.stages.create.useMutation({ onSuccess: aoCriar, onError: aoFalhar });
  const criarModelo = trpc.prosthesis.modelStages.create.useMutation({ onSuccess: aoCriar, onError: aoFalhar });
  const criar = modelo ? criarModelo : criarServico;

  const nomeValido = label.trim().length >= 2;
  const enviar = () => {
    if (!nomeValido || criar.isPending) return;
    criar.mutate({ label: label.trim(), colorHex: cor });
  };

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && onFechar()}>
      {/* O diálogo é aberto de dentro de uma célula da planilha, e o portal do Radix não isola
          eventos do React: sem parar aqui, qualquer clique interno sobe até o `onClick` da linha
          e abre o detalhe do serviço por cima. */}
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        className="max-w-sm border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card"
      >
        <DialogHeader>
          <DialogTitle className="dark:text-foreground" ajuda={AJUDA_PRODUCAO.novoEstagio}>
            {modelo ? "Nova etapa de modelo" : "Novo estágio"}
          </DialogTitle>
          <DialogDescription className="dark:text-muted-foreground">
            {modelo
              ? "Entra na trilha do modelo físico, que anda independente do status do serviço."
              : "Entra na trilha do serviço, na posição escolhida."}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
              Nome
            </span>
            <input
              autoFocus
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && enviar()}
              maxLength={60}
              placeholder="Ex: Acabamento"
              className="h-11 rounded-md border border-zinc-200 bg-white px-3 text-base text-zinc-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 sm:h-9 sm:text-sm dark:border-border dark:bg-card dark:text-foreground"
            />
          </label>

          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-muted-foreground">
              Cor
            </span>
            <SeletorDeCorDoEstagio valor={cor} onChange={setCor} previa={label.trim() || "Novo estágio"} />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-zinc-100 pt-3 dark:border-border">
          <button
            type="button"
            onClick={onFechar}
            className="inline-flex h-11 flex-1 items-center justify-center rounded-md border border-zinc-200 px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 sm:h-9 sm:flex-none dark:border-border dark:text-foreground dark:hover:bg-muted"
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!nomeValido || criar.isPending}
            onClick={enviar}
            className="inline-flex h-11 flex-[1.4] items-center justify-center gap-1.5 rounded-md bg-brand-600 px-4 text-[13px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-60 sm:h-9 sm:flex-none"
          >
            {criar.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {modelo ? "Criar etapa" : "Criar estágio"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
